import type { SupplierProtocol } from '../api';
import { Icon, type IconName } from './icons';
import { Badge } from './ui';

// ============================================================================
// LES TUILES DE PROTOCOLE D'UN FOURNISSEUR (maquette § Fournisseurs) — à choisir dans l'assistant, à lire sur la fiche.
//
// UTILITÉ : dire d'un coup d'œil COMMENT un fournisseur échange — un serveur de fichiers, ou l'e-mail, dont la boîte
// est celle de chaque client. La même rangée sert l'assistant (on choisit) et la fiche (on lit), pour qu'un
// fournisseur par e-mail se reconnaisse partout au même endroit.
// ============================================================================

// Les protocoles d'un fournisseur (maquette § Fournisseurs, branche cible, plus le FTP que la plateforme relève).
// ⚠️ L'AS2 SE MONTRE ET NE SE CHOISIT PAS : aucun pilote ne l'échange encore, et le serveur refuse de le créer — la
//    tuile le dit au lieu de l'offrir (§23.0).
export const PROTOS: ReadonlyArray<{ key: SupplierProtocol; label: string; hint: string; icon: IconName; available: boolean }> = [
  { key: 'sftp', label: 'SFTP', hint: 'Serveur de fichiers, chiffré SSH', icon: 'swap', available: true },
  { key: 'ftps', label: 'FTPS', hint: 'Serveur de fichiers, chiffré TLS', icon: 'swap', available: true },
  { key: 'ftp', label: 'FTP', hint: 'Serveur de fichiers, sans chiffrement', icon: 'swap', available: true },
  { key: 'email', label: 'E-mail', hint: 'Fichiers en pièce jointe, une boîte par client', icon: 'mail', available: true },
  { key: 'as2', label: 'AS2', hint: 'Échange direct signé, avec accusé', icon: 'lock', available: false },
];
/** Ce qu'on choisit dans l'assistant ; un transport à fichiers se configure ici, l'e-mail par client. */
export type Proto = Exclude<SupplierProtocol, 'as2'>;
export type FileProto = Exclude<Proto, 'email'>;
export const protoLabel = (p: string | null | undefined): string => PROTOS.find((x) => x.key === p)?.label ?? (p ? p.toUpperCase() : '—');

/** Les protocoles, celui du fournisseur surligné. Sans `onPick`, elles se lisent seulement (fiche, protocole figé). */
export function ProtoTiles({ value, onPick }: { value: SupplierProtocol | null; onPick?: (p: Proto) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
      {PROTOS.map((p) => {
        const on = value === p.key;
        const body = (
          <>
            <span style={{ width: 30, height: 30, borderRadius: 'var(--r-ctrl)', display: 'grid', placeItems: 'center', flexShrink: 0, background: on ? 'var(--teal-500)' : 'var(--ink-100)', color: on ? '#fff' : 'var(--ink-500)' }}><Icon name={p.icon} size={15} /></span>
            <span style={{ textAlign: 'left', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>{p.label}</span>
              <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.4 }}>{p.hint}</span>
              {!p.available && <span style={{ alignSelf: 'flex-start', marginTop: 3 }}><Badge kind="off">Arrivera plus tard</Badge></span>}
            </span>
          </>
        );
        const st = { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 13px', borderRadius: 'var(--r-sm)', textAlign: 'left' as const, border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`, background: on ? 'var(--teal-50)' : 'var(--paper)', boxShadow: on ? 'none' : 'var(--sh-sm)', opacity: p.available ? 1 : 0.7 };
        // ⚠️ L'AS2 n'est jamais un bouton : désactivé, il resterait un élément qu'on croit pouvoir activer.
        if (!onPick || !p.available) return <div key={p.key} aria-current={on || undefined} style={st}>{body}</div>;
        const pick = p.key as Proto;
        return <button key={p.key} type="button" aria-pressed={on} onClick={() => onPick(pick)} style={{ ...st, cursor: 'pointer', font: 'inherit' }}>{body}</button>;
      })}
    </div>
  );
}
